/* Sui Generis Intelligence — results-only storefront client v1.1.0 */
(() => {
  'use strict';

  const MAX_PHOTOS = 4;
  const ALLOWED_IMAGE = /^image\/(jpeg|png|webp)$/i;
  const BLOCKED_KEY = /(rule|pattern|source|test|logic|method|internal|note|provenance|evidence_url)/i;
  const FIELD_ALIASES = {
    designer: ['designer_name', 'designer', 'brand', 'manufacturer'],
    line: ['brand_line', 'line', 'sub_label', 'sublabel'],
    product: ['product_name', 'product', 'model_name', 'item_name'],
    category: ['category', 'product_type', 'item_type'],
    code: ['visible_code', 'code', 'article_code', 'style_code', 'product_code', 'serial'],
    size: ['size', 'tagged_size'],
    country: ['country_of_origin', 'made_in', 'country'],
    materials: ['material_composition', 'materials', 'composition'],
    color: ['color', 'colour'],
    year: ['verified_year', 'production_year', 'collection_year', 'year'],
    season: ['verified_season', 'season'],
    collection: ['verified_collection', 'collection_name', 'collection'],
  };

  const escapeHtml = (value) => String(value == null ? '' : value)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#039;');

  const clean = (value) => {
    if (value == null) return '';
    if (Array.isArray(value)) return value.map(clean).filter(Boolean).join(', ');
    if (typeof value === 'object') return '';
    return String(value).trim().slice(0, 1000);
  };

  const first = (object, keys) => {
    if (!object || typeof object !== 'object') return '';
    for (const key of keys) {
      if (BLOCKED_KEY.test(key)) continue;
      const value = clean(object[key]);
      if (value) return value;
    }
    return '';
  };

  const scoreNumber = (value) => {
    if (value == null || String(value).trim() === '') return null;
    const numeric = Number(String(value == null ? '' : value).replace('%', '').trim());
    if (Number.isFinite(numeric)) return Math.max(0, Math.min(100, Math.round(numeric)));
    const label = clean(value).toLowerCase();
    if (label === 'confirmed') return 95;
    if (label === 'strong') return 82;
    if (label === 'likely') return 68;
    if (label === 'possible') return 50;
    if (label === 'insufficient') return 20;
    return null;
  };

  const confidenceBand = (score, hasValue = true) => {
    if (!hasValue || score == null) return 'Insufficient';
    if (score >= 90) return 'Confirmed';
    if (score >= 75) return 'Strong';
    if (score >= 60) return 'Likely';
    if (score >= 40) return 'Possible';
    return 'Insufficient';
  };

  const candidateValues = (value) => {
    const list = Array.isArray(value) ? value : [];
    return [...new Set(list.map((item) => clean(item && typeof item === 'object' ? (item.value || item.label || item.name) : item)).filter(Boolean))].slice(0, 4);
  };

  const fieldResult = (analysis, field) => {
    const aliases = FIELD_ALIASES[field] || [field];
    const containers = [analysis.field_results, analysis.confidence_dimensions, analysis.fields].filter((item) => item && typeof item === 'object');
    let wrapped = null;
    for (const container of containers) {
      wrapped = container[field] || aliases.map((alias) => container[alias]).find(Boolean) || null;
      if (wrapped) break;
    }
    if (!wrapped) wrapped = aliases.map((alias) => analysis[alias]).find((value) => value != null && value !== '') || null;

    let value = '';
    let score = null;
    let alternatives = [];
    if (wrapped && typeof wrapped === 'object' && !Array.isArray(wrapped)) {
      value = clean(wrapped.value || wrapped.result || wrapped.label || wrapped.name);
      score = scoreNumber(wrapped.confidence_score ?? wrapped.score ?? wrapped.confidence ?? wrapped.confidence_label);
      alternatives = candidateValues(wrapped.alternatives || wrapped.candidates);
    } else {
      value = clean(wrapped);
    }

    if (!value && field === 'code') value = identifierText(analysis);
    if (score == null) {
      for (const alias of [field, ...aliases]) {
        const candidate = analysis[`${alias}_confidence_score`] ?? analysis[`${alias}_score`] ?? analysis[`${alias}_confidence`];
        score = scoreNumber(candidate);
        if (score != null) break;
      }
    }
    return { value, score, label: confidenceBand(score, Boolean(value)), alternatives };
  };

  const identifierText = (analysis) => {
    const direct = first(analysis, ['visible_code', 'code', 'article_code', 'style_code', 'product_code', 'serial']);
    if (direct) return direct;
    const identifiers = Array.isArray(analysis && analysis.identifiers) ? analysis.identifiers : [];
    return [...new Set(identifiers.map((item) => first(item, ['text', 'code', 'value'])).filter(Boolean))].slice(0, 8).join(', ');
  };

  const boundedResult = (payload, fallbackDesigner = '') => {
    const root = payload && typeof payload === 'object' ? payload : {};
    const analysis = root.result && typeof root.result === 'object'
      ? root.result
      : (root.analysis && typeof root.analysis === 'object' ? root.analysis : root);

    const result = Object.fromEntries(Object.keys(FIELD_ALIASES).map((field) => [field, fieldResult(analysis, field)]));
    if (!result.designer.value && clean(fallbackDesigner)) {
      result.designer = { value: clean(fallbackDesigner), score: 60, label: 'Likely', alternatives: [] };
    }

    const status = first(analysis, ['result_status', 'status']);
    return { result, status };
  };

  const resultHtml = (payload, fallbackDesigner = '') => {
    const bounded = boundedResult(payload, fallbackDesigner);
    const labels = [
      ['Designer', bounded.result.designer], ['Line', bounded.result.line],
      ['Product', bounded.result.product],
      ['Category', bounded.result.category], ['Visible code', bounded.result.code],
      ['Size', bounded.result.size], ['Country', bounded.result.country],
      ['Materials', bounded.result.materials], ['Color', bounded.result.color],
      ['Year', bounded.result.year], ['Season', bounded.result.season],
      ['Collection', bounded.result.collection],
    ].filter((entry) => entry[1] && entry[1].value);

    if (!labels.length) {
      return '<div class="sg-label-intel__warning"><strong>Unknown / insufficient evidence.</strong><p>No bounded result can be given from this input. Add a clear, straight-on photograph of the complete tag.</p></div>';
    }

    const facts = labels.map(([label, field]) =>
      `<div class="sg-label-intel__fact"><span>${escapeHtml(label)}</span><strong>${escapeHtml(field.value)}</strong><small class="sg-label-intel__confidence" data-confidence="${escapeHtml(field.label.toLowerCase())}">${escapeHtml(field.label)}${field.score == null ? '' : ` · ${field.score}%`}</small>${field.alternatives.length ? `<small>Also considered: ${escapeHtml(field.alternatives.join(', '))}</small>` : ''}</div>`
    ).join('');

    return `<section class="sg-label-intel__overview"><div class="sg-label-intel__overview-head"><div><p class="sg-label-intel__kicker">Sui Generis Intelligence</p><h4>Result</h4></div><span class="sg-label-intel__state">Evidence-based result</span></div><div class="sg-label-intel__grid">${facts}</div><details class="sg-label-intel__confidence-key"><summary>What do the confidence labels mean?</summary><div><p><strong>Confirmed · 90–100</strong> Exact, independently verified evidence supports this field.</p><p><strong>Strong · 75–89</strong> Multiple consistent signals support it, with limited uncertainty.</p><p><strong>Likely · 60–74</strong> The best-supported interpretation, but alternatives remain.</p><p><strong>Possible · 40–59</strong> Plausible from partial evidence; use with caution.</p><p><strong>Insufficient · below 40 or unscored</strong> Not enough evidence to state the field reliably.</p></div></details><p class="sg-label-intel__privacy"><strong>Authenticity:</strong> not determined from a tag or code alone. Confidence applies separately to each displayed field.</p></section>`;
  };

  const mergeBounded = (payloads, fallbackDesigner = '') => {
    const merged = Object.fromEntries(Object.keys(FIELD_ALIASES).map((field) => [field, { value: '', score: null, label: 'Insufficient', alternatives: [] }]));
    for (const payload of payloads) {
      const bounded = boundedResult(payload, fallbackDesigner);
      for (const [field, candidate] of Object.entries(bounded.result)) {
        if (!candidate.value) continue;
        const current = merged[field];
        const candidateRank = candidate.score == null ? -1 : candidate.score;
        const currentRank = current.score == null ? -1 : current.score;
        if (!current.value || candidateRank > currentRank) {
          const prior = current.value && current.value !== candidate.value ? [current.value] : [];
          merged[field] = { ...candidate, alternatives: [...new Set([...candidate.alternatives, ...prior])].slice(0, 4) };
        } else if (current.value !== candidate.value) {
          current.alternatives = [...new Set([...current.alternatives, candidate.value])].slice(0, 4);
        }
      }
    }
    return { field_results: merged };
  };

  const toDataUrl = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('read'));
    reader.readAsDataURL(file);
  });

  const post = async (endpoint, body) => {
    if (!endpoint) throw new Error('unavailable');
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    let payload = null;
    try { payload = await response.json(); } catch (_) { payload = null; }
    if (!response.ok || !payload) throw new Error('unavailable');
    return payload;
  };

  const photoRequest = async (scanner, file, role = 'auto', index = 1, total = 1) => {
    const dataUrl = await toDataUrl(file);
    return post(scanner.dataset.endpoint || '', {
      designer_handle: scanner.dataset.designerHintHandle || scanner.dataset.designerHandle || 'auto-detect',
      designer_name: scanner.dataset.designerHintName || scanner.dataset.designerName || 'Unknown designer',
      image_data_url: dataUrl,
      product_context: {
        id: scanner.dataset.productId || '',
        title: scanner.dataset.productTitle || '',
        vendor: scanner.dataset.productVendor || '',
        type: scanner.dataset.productType || '',
        evidence_role: role,
        evidence_index: index,
        evidence_total: total,
      },
    });
  };

  const initLabelScanner = (scanner) => {
    if (scanner.dataset.sgResultsOnlyReady === 'true') return;
    scanner.dataset.sgResultsOnlyReady = 'true';
    const camera = scanner.querySelector('[data-sg-label-camera]');
    const upload = scanner.querySelector('[data-sg-label-upload]');
    const analyze = scanner.querySelector('[data-sg-label-analyze]');
    const clearButton = scanner.querySelector('[data-sg-label-clear]');
    const evidence = scanner.querySelector('[data-sg-label-evidence]');
    const list = scanner.querySelector('[data-sg-label-evidence-list]');
    const count = scanner.querySelector('[data-sg-label-evidence-count]');
    const workspace = scanner.querySelector('[data-sg-label-workspace]');
    const preview = scanner.querySelector('[data-sg-label-preview]');
    const previewImage = scanner.querySelector('[data-sg-label-preview-image]');
    const previewList = scanner.querySelector('[data-sg-label-preview-list]');
    const status = scanner.querySelector('[data-sg-label-status]');
    const results = scanner.querySelector('[data-sg-label-results]');
    const state = [];

    const renderQueue = () => {
      if (!evidence || !list) return;
      evidence.hidden = state.length === 0;
      if (count) count.textContent = `${state.length} of ${MAX_PHOTOS} photos`;
      if (analyze) analyze.disabled = state.length === 0;
      if (clearButton) clearButton.disabled = state.length === 0;
      list.innerHTML = state.map((entry, index) => `<article class="sg-label-intel__evidence-item"><img src="${escapeHtml(entry.preview)}" alt="Selected evidence photo ${index + 1}"><div><strong>Photo ${index + 1}</strong><small>${escapeHtml(entry.file.name || 'Selected photo')}</small><label>Evidence type<select data-results-role="${index}"><option value="auto">Auto / not specified</option><option value="brand">Brand / neck label</option><option value="care">Care / product label</option><option value="code">Code / stamp / identifier</option></select></label></div><button type="button" data-results-remove="${index}">Remove</button></article>`).join('');
      list.querySelectorAll('[data-results-role]').forEach((select) => select.addEventListener('change', () => {
        const item = state[Number(select.dataset.resultsRole)];
        if (item) item.role = select.value || 'auto';
      }));
      list.querySelectorAll('[data-results-remove]').forEach((button) => button.addEventListener('click', () => {
        const index = Number(button.dataset.resultsRemove);
        if (state[index]) URL.revokeObjectURL(state[index].preview);
        state.splice(index, 1);
        renderQueue();
      }));
    };

    const showResult = async (entries) => {
      if (!entries.length || !workspace || !status || !results) return;
      workspace.hidden = false;
      results.hidden = true;
      status.dataset.state = 'working';
      status.textContent = 'Analyzing the submitted tag evidence…';
      if (preview) preview.hidden = false;
      if (previewImage && entries[0]) previewImage.src = entries[0].preview;
      if (previewList) previewList.innerHTML = entries.map((entry, index) => `<figure class="sg-label-intel__preview-item"><img src="${escapeHtml(entry.preview)}" alt="Evidence photo ${index + 1}"><figcaption>Evidence photo ${index + 1}</figcaption></figure>`).join('');
      try {
        const payloads = [];
        for (let index = 0; index < entries.length; index += 1) {
          payloads.push(await photoRequest(scanner, entries[index].file, entries[index].role, index + 1, entries.length));
        }
        const merged = mergeBounded(payloads, scanner.dataset.designerHintName || scanner.dataset.productVendor || '');
        status.dataset.state = 'done';
        status.textContent = 'Analysis complete.';
        results.innerHTML = resultHtml({ result: merged }, scanner.dataset.designerHintName || scanner.dataset.productVendor || '');
      } catch (_) {
        status.dataset.state = 'error';
        status.textContent = 'A verified result is not available for this input.';
        results.innerHTML = resultHtml({}, '');
      }
      results.hidden = false;
    };

    const addFiles = (files, immediate = false) => {
      const valid = Array.from(files || []).filter((file) => ALLOWED_IMAGE.test(file.type || ''));
      const added = valid.slice(0, Math.max(0, MAX_PHOTOS - state.length)).map((file) => ({ file, role: 'auto', preview: URL.createObjectURL(file) }));
      state.push(...added);
      renderQueue();
      if (immediate && added[0]) showResult([added[0]]);
    };

    const multiMode = Boolean(analyze);
    if (camera) camera.addEventListener('change', () => { addFiles(camera.files, !multiMode); camera.value = ''; });
    if (upload) upload.addEventListener('change', () => { addFiles(upload.files, !multiMode); upload.value = ''; });
    if (analyze) analyze.addEventListener('click', () => showResult([...state]));
    if (clearButton) clearButton.addEventListener('click', () => {
      state.forEach((entry) => URL.revokeObjectURL(entry.preview));
      state.length = 0;
      renderQueue();
      if (workspace) workspace.hidden = true;
      if (results) { results.hidden = true; results.innerHTML = ''; }
      if (status) status.textContent = '';
    });
    renderQueue();
  };

  const initCareScanner = (scanner) => {
    if (scanner.dataset.sgResultsOnlyReady === 'true') return;
    scanner.dataset.sgResultsOnlyReady = 'true';
    const inputs = [scanner.querySelector('[data-sg-care-camera]'), scanner.querySelector('[data-sg-care-upload]')].filter(Boolean);
    const workspace = scanner.querySelector('[data-sg-care-workspace]');
    const preview = scanner.querySelector('[data-sg-care-preview]');
    const image = scanner.querySelector('[data-sg-care-preview-image]');
    const status = scanner.querySelector('[data-sg-care-status]');
    const results = scanner.querySelector('[data-sg-care-results]');
    const run = async (file) => {
      if (!file || !ALLOWED_IMAGE.test(file.type || '')) return;
      const previewUrl = URL.createObjectURL(file);
      if (workspace) workspace.hidden = false;
      if (preview) preview.hidden = false;
      if (image) image.src = previewUrl;
      if (status) { status.dataset.state = 'working'; status.textContent = 'Analyzing the submitted tag…'; }
      if (results) results.hidden = true;
      try {
        const payload = await photoRequest(scanner, file);
        if (status) { status.dataset.state = 'done'; status.textContent = 'Analysis complete.'; }
        if (results) results.innerHTML = resultHtml(payload, scanner.dataset.designerName || '');
      } catch (_) {
        if (status) { status.dataset.state = 'error'; status.textContent = 'A verified result is not available for this input.'; }
        if (results) results.innerHTML = resultHtml({}, '');
      }
      if (results) results.hidden = false;
    };
    inputs.forEach((input) => input.addEventListener('change', () => { if (input.files && input.files[0]) run(input.files[0]); input.value = ''; }));
  };

  const submitCodeTool = async (tool, event) => {
    if (event) event.preventDefault();
    if (!tool || tool.dataset.sgCodeRunning === 'true') return;
    const form = tool.querySelector('[data-sg-results-only-code-form]');
    const input = tool.querySelector('[data-sg-results-only-code-input]');
    const status = tool.querySelector('[data-sg-results-only-code-status]');
    const results = tool.querySelector('[data-sg-results-only-code-result]');
    if (!form || !input || !results) return;
    const code = String(input.value || '').trim().slice(0, 300);
    if (!code) return;
    tool.dataset.sgCodeRunning = 'true';
    if (status) status.textContent = 'Checking the submitted reference…';
    results.innerHTML = '';
    try {
      const payload = await post(tool.dataset.endpoint || '', {
        analysis_mode: 'code_only',
        visible_code: code,
        designer_handle: tool.dataset.designerHandle || 'auto-detect',
        designer_name: tool.dataset.designerName || 'Unknown designer',
      });
      results.innerHTML = resultHtml(payload, tool.dataset.designerName || '');
      if (status) status.textContent = 'Analysis complete.';
    } catch (_) {
      results.innerHTML = '<div class="sg-label-intel__warning"><strong>Unknown / insufficient evidence.</strong><p>A secure code-only result is not available yet. Upload a clear photograph of the complete tag for analysis.</p></div>';
      if (status) status.textContent = 'No verified code-only result is available.';
    } finally {
      delete tool.dataset.sgCodeRunning;
    }
  };

  const initCodeTool = (tool) => {
    if (tool.dataset.sgResultsOnlyReady === 'true') return;
    const form = tool.querySelector('[data-sg-results-only-code-form]');
    const input = tool.querySelector('[data-sg-results-only-code-input]');
    const results = tool.querySelector('[data-sg-results-only-code-result]');
    if (!form || !input || !results) return;
    tool.dataset.sgResultsOnlyReady = 'true';
    form.addEventListener('submit', (event) => submitCodeTool(tool, event));
    const submitButton = form.querySelector('button[type="submit"]');
    if (submitButton) submitButton.addEventListener('click', (event) => submitCodeTool(tool, event));
  };

  const initialize = (scope = document) => {
    scope.querySelectorAll?.('[data-sg-label-intel]').forEach(initLabelScanner);
    scope.querySelectorAll?.('[data-sg-care-scanner]').forEach(initCareScanner);
    scope.querySelectorAll?.('[data-sg-results-only-code]').forEach(initCodeTool);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  else initialize();
  if (typeof window !== 'undefined') window.setTimeout(() => initialize(), 500);
  if (document.documentElement && !document.documentElement.dataset.sgResultsOnlyDelegated) {
    document.documentElement.dataset.sgResultsOnlyDelegated = 'true';
    document.addEventListener('click', (event) => {
      const button = event.target.closest?.('[data-sg-results-only-code-form] button[type="submit"]');
      const tool = button?.closest?.('[data-sg-results-only-code]');
      if (tool) submitCodeTool(tool, event);
    });
    document.addEventListener('submit', (event) => {
      const form = event.target.closest?.('[data-sg-results-only-code-form]');
      const tool = form?.closest?.('[data-sg-results-only-code]');
      if (tool) submitCodeTool(tool, event);
    }, true);
  }
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
